import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { formatCurrency } from '../utils/currency';

export default function Admin() {
  const { currentUser, userData } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalRaffles: 0,
    totalRevenue: 0,
    totalUsers: 0,
    activeRaffles: 0
  });
  const [recentRaffles, setRecentRaffles] = useState([]);
  const [recentPurchases, setRecentPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    } else if (userData && userData.role !== 'admin') {
      navigate('/');
    }
  }, [currentUser, userData, navigate]);

  useEffect(() => {
    const fetchAdminData = async () => {
      if (!currentUser || !userData || userData.role !== 'admin') return;

      try {
        // Fetch all raffles
        const rafflesSnap = await getDocs(collection(db, 'raffles'));
        const allRaffles = rafflesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const totalRaffles = allRaffles.length;
        const activeRaffles = allRaffles.filter(r => r.status === 'active').length;
        const totalRevenue = allRaffles.reduce((sum, r) => sum + (r.totalRevenue || 0), 0);

        // Fetch users count
        const usersSnap = await getDocs(collection(db, 'users'));
        const totalUsers = usersSnap.docs.length;

        setStats({ totalRaffles, totalRevenue, totalUsers, activeRaffles });

        // Recent raffles (sorted by createdAt)
        const sortedRaffles = [...allRaffles]
          .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
          .slice(0, 10);
        setRecentRaffles(sortedRaffles);

        // Fetch recent purchases
        try {
          const purchasesQuery = query(
            collection(db, 'purchases'),
            orderBy('createdAt', 'desc'),
            limit(20)
          );
          const purchasesSnap = await getDocs(purchasesQuery);
          const purchases = purchasesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setRecentPurchases(purchases);
        } catch (err) {
          console.log('No purchases collection yet:', err.message);
          setRecentPurchases([]);
        }
      } catch (error) {
        console.error('Error fetching admin data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, [currentUser, userData]);

  if (!currentUser || !userData || userData.role !== 'admin') {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        textAlign: 'center'
      }}>
        <div>
          <h2 style={{ color: 'var(--danger)' }}>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', fontSize: '1.5rem' }}>
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* Header */}
      <div className="admin-header">
        <h1>{t('admin.dashboard')}</h1>
        <p style={{ color: 'var(--dark-light)', marginTop: '0.5rem' }}>
          Welcome back, {currentUser.displayName || currentUser.email}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="admin-stats">
        <div className="admin-stat-card">
          <div className="stat-icon">📋</div>
          <div className="stat-value">{stats.totalRaffles}</div>
          <div className="stat-label">{t('admin.totalRaffles')}</div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-value">R$ {stats.totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
          <div className="stat-label">{t('admin.totalRevenue')}</div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-value">{stats.totalUsers}</div>
          <div className="stat-label">{t('admin.totalUsers')}</div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-value">{stats.activeRaffles}</div>
          <div className="stat-label">{t('admin.activeRaffles')}</div>
        </div>
      </div>

      {/* Recent Raffles Table */}
      <div className="admin-table-card">
        <h2>{t('admin.recentActivity')} — Raffles</h2>

        {recentRaffles.length > 0 ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('raffle.title')}</th>
                <th>Creator</th>
                <th>{t('raffle.status')}</th>
                <th>{t('raffle.sold')}</th>
                <th>{t('admin.totalRevenue')}</th>
                <th>{t('common.edit')}</th>
              </tr>
            </thead>
            <tbody>
              {recentRaffles.map(raffle => (
                <tr key={raffle.id}>
                  <td>
                    <strong>{raffle.title}</strong>
                  </td>
                  <td>{raffle.createdByName || 'Unknown'}</td>
                  <td>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.3rem 0.8rem',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      background: raffle.status === 'active'
                        ? 'var(--success)'
                        : raffle.status === 'completed'
                        ? 'var(--primary)'
                        : 'var(--gray-300)',
                      color: raffle.status === 'active' || raffle.status === 'completed'
                        ? 'var(--white)'
                        : 'var(--dark)'
                    }}>
                      {raffle.status ? raffle.status.charAt(0).toUpperCase() + raffle.status.slice(1) : 'Unknown'}
                    </span>
                  </td>
                  <td>
                    <strong>{raffle.numbersSold || 0}</strong>
                    {raffle.maxNumbers ? ` / ${raffle.maxNumbers}` : ''}
                  </td>
                  <td>
                    {raffle.currency === 'BRL' ? 'R$' : '$'} {(raffle.totalRevenue || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </td>
                  <td>
                    <Link
                      to={`/raffle/${raffle.id}`}
                      style={{
                        background: 'var(--primary)',
                        color: 'var(--white)',
                        border: 'none',
                        padding: '0.4rem 0.8rem',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        textDecoration: 'none',
                        display: 'inline-block'
                      }}
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ textAlign: 'center', color: 'var(--dark-light)', padding: '2rem' }}>
            No raffles created yet.
          </p>
        )}
      </div>

      {/* Recent Purchases */}
      {recentPurchases.length > 0 && (
        <div className="admin-table-card" style={{ marginTop: '2rem' }}>
          <h2>Recent Purchases</h2>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Raffle</th>
                <th>Buyer</th>
                <th>Numbers</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentPurchases.map(purchase => (
                <tr key={purchase.id}>
                  <td><strong>{purchase.raffleTitle || 'Unknown'}</strong></td>
                  <td>{purchase.buyerName || purchase.buyerEmail || 'Unknown'}</td>
                  <td>{(purchase.selectedNumbers || []).join(', ')}</td>
                  <td>
                    {formatCurrency(purchase.totalAmount || 0, purchase.currency || 'BRL')}
                  </td>
                  <td>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.3rem 0.8rem',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      background: purchase.status === 'confirmed'
                        ? 'var(--success)'
                        : purchase.status === 'pending'
                        ? 'var(--warning, #f39c12)'
                        : 'var(--danger)',
                      color: 'var(--white)'
                    }}>
                      {purchase.status ? purchase.status.charAt(0).toUpperCase() + purchase.status.slice(1) : 'Unknown'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
